import { useCallback } from 'react';
import { servicesAPI, adminAPI } from '@/services/api';
import { User } from '@/types';
import { getErrorMessage } from '@/utils/errorUtils';

interface UseCustomerUserActionsProps {
  customerId: number | undefined;
  isAdmin: boolean;
  setError: (message: string) => void;
  setSuccess: (message: string) => void;
  fetchUsers: () => Promise<void> | void;
  fetchSubscribedServices: () => Promise<void>;
  handleSearch: (query: string, category: string) => Promise<void>;
  setSubscribedServiceIds: React.Dispatch<React.SetStateAction<Set<number>>>;
  activeTab: string;
  searchResults: any[];
}

export const useCustomerUserActions = ({
  customerId,
  isAdmin,
  setError,
  setSuccess,
  fetchUsers,
  fetchSubscribedServices,
  handleSearch,
  setSubscribedServiceIds,
  activeTab,
  searchResults,
}: UseCustomerUserActionsProps) => {
  // 현재 사용자 정보를 로컬 스토리지에서 읽어 읽기전용 여부 확인
  let isReadonlyUser = false;
  try {
    const rawUser = localStorage.getItem('user');
    if (rawUser) {
      const parsed: User = JSON.parse(rawUser);
      isReadonlyUser = parsed.permission === 'readonly';
    }
  } catch {
    // 파싱 실패 시 읽기전용 판단은 false로 둔다.
  }
  const handleSubscribe = useCallback(async (serviceSlug: string) => {
    // 읽기 전용 사용자는 구독 불가
    if (isReadonlyUser) {
      setError('읽기 전용 계정은 새로운 서비스를 구독할 수 없습니다.');
      return;
    }

    // 일반 사용자도 구독 가능하되, 현재 구현에서는 admin 전용 구독으로 제한
    if (!isAdmin) {
      setError('관리자만 서비스를 구독할 수 있습니다.');
      return;
    }

    try {
      await servicesAPI.subscribeToService(serviceSlug);
      setSuccess('서비스 구독이 완료되었습니다.');
    } catch (error: any) {
      // 409 = 이미 구독 중 → 목표 달성이므로 성공 처리
      if (error?.response?.status === 409) {
        setSuccess('서비스가 이미 구독되어 있습니다.');
      } else {
        console.error('Failed to subscribe:', error);
        setError(getErrorMessage(error, '서비스 구독에 실패했습니다.'));
        throw error;
      }
    }
    // 구독 서비스 목록 새로고침 (성공/409 모두)
    await fetchSubscribedServices();
    if (activeTab === 'search' && searchResults.length > 0) {
      const queryInput = document.querySelector<HTMLInputElement>('input[id="search_query"]');
      const categoryInput = document.querySelector<HTMLInputElement>('input[id="search_category"]');
      const query = queryInput?.value || '';
      const category = categoryInput?.value || '';
      await handleSearch(query, category);
    }
  }, [isAdmin, isReadonlyUser, setError, setSuccess, fetchSubscribedServices, handleSearch, setSubscribedServiceIds, activeTab, searchResults]);

  const handleUnsubscribe = useCallback(async (serviceSlug: string) => {
    // 읽기 전용 사용자는 구독 해제도 불가 (관리자만)
    if (!isAdmin) {
      setError('관리자만 서비스 구독을 해제할 수 있습니다.');
      return;
    }

    try {
      await servicesAPI.unsubscribeFromService(serviceSlug);
      setSuccess('서비스 구독이 해제되었습니다.');
      await fetchSubscribedServices();
      // Note: setSubscribedServiceIds는 내부 상태 관리용이므로 fetchSubscribedServices가 처리
    } catch (error: unknown) {
      console.error('Failed to unsubscribe:', error);
      setError(getErrorMessage(error, '서비스 구독 해제에 실패했습니다.'));
      throw error;
    }
  }, [isAdmin, setError, setSuccess, fetchSubscribedServices]);

  const handleServiceClick = useCallback((service: any, event?: React.MouseEvent) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    return service;
  }, []);

  const handleServiceConnect = useCallback(async (service: any) => {
    try {
      // redirectToService API를 통해 구독 체크 및 리다이렉트 URL 받기
      const { servicesAPI } = await import('@/services/api');
      const result = await servicesAPI.redirectToService(service.service_slug);
      const redirectUrl = result.data?.redirect_url;

      if (redirectUrl) {
        // 클릭 핸들러에서 직접 호출하므로 팝업 차단 안 됨
        window.open(redirectUrl, '_blank');
      } else {
        throw new Error('리다이렉트 URL을 받지 못했습니다.');
      }
    } catch (error: any) {
      console.error('Failed to redirect to service:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || error?.message;

      if (status === 403) {
        if (detail?.includes('활성 구독')) {
          throw new Error('이 서비스에 대한 활성 구독이 없습니다. 구독 관리에서 구독을 활성화해주세요.');
        } else if (detail?.includes('연결되지 않은')) {
          throw new Error('이 서비스는 아직 연결되지 않았습니다. 먼저 구독해주세요.');
        } else {
          throw new Error(detail || '서비스 접근 권한이 없습니다.');
        }
      } else if (status === 402) {
        throw new Error('크레딧이 부족합니다. 크레딧을 충전해주세요.');
      } else {
        throw new Error(detail || '서비스로 이동하는데 실패했습니다.');
      }
    }
  }, []);

  const handleInviteUser = useCallback(async (email: string, role: 'admin' | 'user') => {
    if (!customerId) return;

    try {
      await adminAPI.inviteUser(email, customerId, role);
      setSuccess('사용자가 초대되었습니다.');
      await fetchUsers();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '사용자 초대에 실패했습니다.'));
    }
  }, [customerId, setError, setSuccess, fetchUsers]);

  const handleApproveUser = useCallback(async (userId: number) => {
    try {
      await adminAPI.approveUser(userId);
      setSuccess('사용자가 승인되었습니다.');
      await fetchUsers();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '사용자 승인에 실패했습니다.'));
    }
  }, [setError, setSuccess, fetchUsers]);

  const handleDeactivateUser = useCallback(async (userId: number) => {
    try {
      await adminAPI.deactivateUser(userId);
      setSuccess('사용자가 비활성화되었습니다.');
      await fetchUsers();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '사용자 비활성화에 실패했습니다.'));
      throw error;
    }
  }, [setError, setSuccess, fetchUsers]);

  const handleDeleteUser = useCallback(async (userId: number) => {
    try {
      setError('');
      await adminAPI.deleteUser(userId);
      setSuccess('사용자가 삭제되었습니다.');
      await fetchUsers();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '사용자 삭제에 실패했습니다.'));
      throw error;
    }
  }, [setError, setSuccess, fetchUsers]);

  const handleUpdateUser = useCallback(async (userId: number, role: 'admin' | 'user', permission?: 'readwrite' | 'readonly') => {
    try {
      setError('');
      await adminAPI.updateUser(userId, {
        role,
        permission: role === 'user' ? permission : undefined,
        status: 'active'
      });
      setSuccess('사용자 정보가 수정되었습니다.');
      await fetchUsers();
    } catch (error: unknown) {
      setError(getErrorMessage(error, '사용자 수정에 실패했습니다.'));
    }
  }, [setError, setSuccess, fetchUsers]);

  return {
    handleSubscribe,
    handleUnsubscribe,
    handleServiceClick,
    handleServiceConnect,
    handleInviteUser,
    handleApproveUser,
    handleDeactivateUser,
    handleDeleteUser,
    handleUpdateUser,
  };
};

